import React from 'react'
import { Box, Typography, Grid } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { LawsuitPerson, LawsuitsCheckDetails } from './types'
import { formatDate, ObjectDetails } from '../ObjectDetails'
import CollapsibleSection from '../CollapsableSection'

interface Props {
  data: LawsuitsCheckDetails
  theme: any
}

const capitalizeFirst = (str: string = ''): string => {
  if (!str) return ''
  const [first, ...rest] = str
  return first.toUpperCase() + rest.join('').toLowerCase()
}

const formatField = (value?: string) => {
  if (!value) return ''
  const capitalized = capitalizeFirst(value)
  return capitalized
}

const LawsuitContent: React.FC<{ lawsuit: LawsuitPerson }> = ({ lawsuit }) => {
  const { t } = useTranslation(['common'])
  const simpleDetails = {
    Number: lawsuit.Number,
    Type: formatField(lawsuit.Type),
    MainSubject: formatField(lawsuit.MainSubject),
    CourtName: formatField(lawsuit.CourtName),
    CourtLevel: formatField(lawsuit.CourtLevel),
    CourtType: formatField(lawsuit.CourtType),
    CourtDistrict: formatField(lawsuit.CourtDistrict),
    Judge: formatField(lawsuit.Judge),
    JudgingBody: formatField(lawsuit.JudgingBody),
    State: formatField(lawsuit.State),
    Status: formatField(lawsuit.Status),
    Value:
      lawsuit.Value !== undefined ? lawsuit.Value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '',
    LastUpdate: lawsuit.LastUpdate ? formatDate(lawsuit.LastUpdate) : '',
    AverageNumberOfUpdatesPerMonth: lawsuit.AverageNumberOfUpdatesPerMonth,
    LawSuitAge: lawsuit.LawSuitAge,
  }

  return (
    <Box>
      <ObjectDetails data={simpleDetails} />

      {lawsuit.RelatedLawsuits?.length > 0 && (
        <CollapsibleSection title={`${t('common:lawsuits.RelatedLawsuits')} (${lawsuit.RelatedLawsuits.length})`}>
          {lawsuit.RelatedLawsuits.map((related, index) => (
            <Typography key={index} variant="text2">
              {related}
            </Typography>
          ))}
        </CollapsibleSection>
      )}

      {lawsuit.OtherSubjects?.length > 0 && (
        <CollapsibleSection title={`${t('common:lawsuits.OtherSubjectsTitle')} (${lawsuit.OtherSubjects.length})`}>
          {lawsuit.OtherSubjects.map((subject) => (
            <Typography
              key={subject}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={subject}
            >
              {subject}
            </Typography>
          ))}
        </CollapsibleSection>
      )}

      {lawsuit.Parties?.length > 0 && (
        <CollapsibleSection title={`${t('common:lawsuits.PartiesTitle')} (${lawsuit.Parties.length})`}>
          {Object.entries(
            lawsuit.Parties.reduce((acc, party) => {
              acc[party.Type] = acc[party.Type] || []
              acc[party.Type].push(party)
              return acc
            }, {} as Record<string, typeof lawsuit.Parties>),
          ).map(([type, parties]) => (
            <CollapsibleSection key={type} title={`${t('common:lawsuits.Type')}: ${type}`}>
              {parties.map((party, idx) => (
                <CollapsibleSection
                  key={party.Name ? party.Name : idx}
                  title={
                    party.Name
                      ? `${t('common:lawsuits.Name')}: ${party.Name}`
                      : `${t('common:lawsuits.Party')} ${idx + 1}`
                  }
                >
                  <ObjectDetails data={party} />
                </CollapsibleSection>
              ))}
            </CollapsibleSection>
          ))}
        </CollapsibleSection>
      )}

      {lawsuit.Updates?.length > 0 && (
        <CollapsibleSection title={`${t('common:lawsuits.UpdatesTitle')} (${lawsuit.Updates.length})`}>
          {lawsuit.Updates.map((update, index) => (
            <CollapsibleSection
              key={index}
              title={`${t('common:lawsuits.Published')}: ${formatDate(update.PublishDate)}`}
            >
              <ObjectDetails data={update} />
            </CollapsibleSection>
          ))}
        </CollapsibleSection>
      )}

      {lawsuit.Petitions?.length > 0 && (
        <CollapsibleSection title={`${t('common:lawsuits.PetitionsTitle')} (${lawsuit.Petitions.length})`}>
          {lawsuit.Petitions.map((petition, index) => (
            <CollapsibleSection
              key={index}
              title={`${t('common:lawsuits.Type')}: ${petition.Type} - ${formatDate(petition.CreationDate)}`}
            >
              <ObjectDetails data={petition} />
            </CollapsibleSection>
          ))}
        </CollapsibleSection>
      )}

      {lawsuit.Decisions?.length > 0 && (
        <CollapsibleSection title={`${t('common:lawsuits.DecisionsTitle')} (${lawsuit.Decisions.length})`}>
          {lawsuit.Decisions.map((decision, index) => (
            <CollapsibleSection
              key={index}
              title={`${t('common:lawsuits.Date')}: ${formatDate(decision.DecisionDate)}`}
            >
              <ObjectDetails data={decision} />
            </CollapsibleSection>
          ))}
        </CollapsibleSection>
      )}
    </Box>
  )
}

const LawsuitsPersonCheck: React.FC<Props> = ({ data, theme }) => {
  const { t } = useTranslation(['common'])

  const getFirstLawsuitDate = () => {
    if (!data.FirstLawsuitDate) return '---'
    const formattedDate = formatDate(data.FirstLawsuitDate)
    return formattedDate.endsWith('1901') ? '---' : formattedDate
  }

  const getLastLawsuitDate = () => {
    if (!data.LastLawsuitDate) return '---'
    const formattedDate = formatDate(data.LastLawsuitDate)
    return formattedDate.endsWith('9999') ? '---' : formattedDate
  }

  return (
    <Grid
      container
      spacing={2}
      className="accordion-jap"
      margin={0}
      width="100%"
      sx={{ backgroundColor: theme === 'light' ? '#FCFFFD' : '#020D06', overflowY: 'auto' }}
    >
      <Grid
        item
        xs={6}
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', pl: 2 }}
      >
        <Box sx={{ width: '100%', maxWidth: 600, minWidth: 350 }}>
          <Typography variant="h4" gutterBottom>
            {t('common:lawsuits.Totals')}
          </Typography>
          <Grid container spacing={2} sx={{ pl: 1 }}>
            <Grid item xs={6}>
              <Typography variant="body2">
                {t('common:lawsuits.TotalLawsuits')}: {data.TotalLawsuits}
              </Typography>
              <Typography variant="body2">
                {t('common:lawsuits.TotalLawsuitsAsAuthor')}: {data.TotalLawsuitsAsAuthor}
              </Typography>
              <Typography variant="body2">
                {t('common:lawsuits.TotalLawsuitsAsDefendant')}: {data.TotalLawsuitsAsDefendant}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                {t('common:lawsuits.TotalLawsuitsAsOther')}: {data.TotalLawsuitsAsOther}
              </Typography>
              <Typography variant="body2">
                {t('common:lawsuits.FirstLawsuitDate')}: {getFirstLawsuitDate()}
              </Typography>
              <Typography variant="body2">
                {t('common:lawsuits.LastLawsuitDate')}: {getLastLawsuitDate()}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid
        item
        xs={6}
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', pl: 2 }}
      >
        <Box sx={{ width: '100%', maxWidth: 600, minWidth: 350 }}>
          <Typography variant="h4" gutterBottom>
            {t('common:lawsuits.QuantityByDays')}
          </Typography>
          <Grid container spacing={1} sx={{ pl: 1 }}>
            <Grid item xs={12} sm={6} md={12} lg={6}>
              <Typography variant="body2">
                {t('common:lawsuits.Last30DaysLawsuits')}: {data.Last30DaysLawsuits}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={12} lg={6}>
              <Typography variant="body2">
                {t('common:lawsuits.Last90DaysLawsuits')}: {data.Last90DaysLawsuits}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={12} lg={6}>
              <Typography variant="body2">
                {t('common:lawsuits.Last180DaysLawsuits')}: {data.Last180DaysLawsuits}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={12} lg={6}>
              <Typography variant="body2">
                {t('common:lawsuits.Last365DaysLawsuits')}: {data.Last365DaysLawsuits}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <CollapsibleSection title={`${t('common:lawsuits.LawsuitList')} (${data.Lawsuits?.length || 0})`}>
          {(() => {
            const groupedByCourtType =
              data.Lawsuits?.reduce((acc, lawsuit) => {
                acc[lawsuit.CourtType] = acc[lawsuit.CourtType] || []
                acc[lawsuit.CourtType].push(lawsuit)
                return acc
              }, {} as Record<string, LawsuitPerson[]>) || {}

            return Object.entries(groupedByCourtType).map(([courtType, lawsuitsArr]) => {
              const groupedByMainSubject = lawsuitsArr.reduce((acc, lawsuit) => {
                acc[lawsuit.MainSubject] = acc[lawsuit.MainSubject] || []
                acc[lawsuit.MainSubject].push(lawsuit)
                return acc
              }, {} as Record<string, LawsuitPerson[]>)

              return (
                <CollapsibleSection
                  key={courtType}
                  title={
                    <Typography sx={{ whiteSpace: 'normal', wordBreak: 'break-word', fontWeight: 'bold' }}>
                      {`${formatField(courtType)} (${lawsuitsArr.length})`}
                    </Typography>
                  }
                >
                  {Object.entries(groupedByMainSubject).map(([mainSubject, subjectLawsuits]) => (
                    <Box key={mainSubject} sx={{ mb: 1 }}>
                      <CollapsibleSection
                        title={
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              width: '100%',
                            }}
                          >
                            <Typography
                              title={formatField(mainSubject)}
                              sx={{
                                fontWeight: 'bold',
                                flex: 1,
                                minWidth: 0,
                                mr: 1,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                              }}
                            >
                              {`${formatField(mainSubject)} (${subjectLawsuits.length})`}
                            </Typography>
                          </Box>
                        }
                      >
                        {subjectLawsuits.map((lawsuit) => (
                          <CollapsibleSection
                            key={lawsuit.Number}
                            title={
                              <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{`${t(
                                'common:action',
                              )} (${lawsuit.Number})`}</span>
                            }
                          >
                            <LawsuitContent lawsuit={lawsuit} />
                          </CollapsibleSection>
                        ))}
                      </CollapsibleSection>
                    </Box>
                  ))}
                </CollapsibleSection>
              )
            })
          })()}
        </CollapsibleSection>
      </Grid>
    </Grid>
  )
}

export default LawsuitsPersonCheck
